/**
 * Acceptation des CGV/CGU et du DPA — règles PURES partagées par l'écran ET le serveur
 * (plan `docs/plans/feat-acceptation-cgv-dpa.md`, lot 3).
 *
 * ⚠️ Le texte des cases vit ICI et nulle part ailleurs, volontairement hors des fichiers de
 * traduction : c'est une pièce probante. L'écran l'affiche, le serveur le RECOMPOSE avec la même
 * fonction et les mêmes données (raison sociale) et l'enregistre — jamais le texte envoyé
 * par le navigateur. Modifier une phrase change ce que les clients acceptent : décision de Valérie.
 *
 * Le français fait foi (décision du 07/10/2026) : en anglais, la case le rappelle.
 */
import type { AcceptableLegalKind } from "./legal-documents";

/** Documents qui ont une case à l'inscription. L'annexe Module RH aura la sienne avec son parcours. */
export type SignupLegalKind = Exclude<AcceptableLegalKind, "rh_annex">;

export type LegalTextLanguage = "fr" | "en";

/** Langue d'affichage → langue du texte probant (tout ce qui n'est pas l'anglais est le français). */
export function legalTextLanguage(lang: unknown): LegalTextLanguage {
  return typeof lang === "string" && lang.toLowerCase().startsWith("en") ? "en" : "fr";
}

/** Une phrase de case : le nom du document (`link`) porte le lien vers le PDF, au milieu du texte. */
export interface LegalCheckboxParts {
  before: string;
  link: string;
  after: string;
}

const EN_FRENCH_BINDING = " Only the French version is binding; the English text is a courtesy translation.";

// Formulation décidée par Valérie le 07/10/2026 : pas de numéro de version dans la phrase (la
// version et l'empreinte du PDF sont enregistrées à part), lien sur le seul nom du document.
const TEXTS: Record<LegalTextLanguage, Record<SignupLegalKind, (company: string) => LegalCheckboxParts>> = {
  fr: {
    cgv: (company) => ({
      before: `Je déclare disposer du pouvoir d'engager la société ${company} et j'accepte, en son nom, les `,
      link: "Conditions générales de vente et d'utilisation",
      after: " de BizCoach.",
    }),
    dpa: (company) => ({
      before: `J'ai lu et j'accepte, au nom de la société ${company}, l'`,
      link: "accord de sous-traitance des données à caractère personnel (DPA)",
      after: " de BizCoach.",
    }),
  },
  en: {
    cgv: (company) => ({
      before: `I declare that I am authorised to bind the company ${company} and, on its behalf, I accept the `,
      link: "General Terms and Conditions of Sale and Use",
      after: ` of BizCoach.${EN_FRENCH_BINDING}`,
    }),
    dpa: (company) => ({
      before: `I have read and, on behalf of the company ${company}, I accept the `,
      link: "Data Processing Agreement (DPA)",
      after: ` of BizCoach.${EN_FRENCH_BINDING}`,
    }),
  },
};

/** Phrase d'une case découpée autour du lien (affichage). */
export function legalCheckboxParts(kind: SignupLegalKind, lang: LegalTextLanguage, p: { company: string }): LegalCheckboxParts {
  return TEXTS[lang][kind](p.company.trim());
}

/** Texte exact d'une case, tel qu'affiché et tel qu'enregistré (les trois morceaux, bout à bout). */
export function legalCheckboxText(kind: SignupLegalKind, lang: LegalTextLanguage, p: { company: string }): string {
  const { before, link, after } = legalCheckboxParts(kind, lang, p);
  return `${before}${link}${after}`;
}

/** SIREN saisi → 9 chiffres sans espaces ni points (ou la saisie nettoyée si elle n'en a pas 9). */
export function normalizeSiren(raw: unknown): string {
  return typeof raw === "string" ? raw.replace(/[\s.\-]/g, "") : "";
}

/**
 * Un SIREN est-il valide ? 9 chiffres et clé de Luhn, qui attrape les fautes de frappe. Ne dit pas
 * que la société existe (la vérification au répertoire Sirene est hors de ce chantier).
 */
export function isValidSiren(raw: unknown): boolean {
  const s = normalizeSiren(raw);
  if (!/^\d{9}$/.test(s)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    let d = Number(s[i]);
    if (i % 2 === 1) { d *= 2; if (d > 9) d -= 9; }
    sum += d;
  }
  return sum % 10 === 0;
}

/** Longueur maximale d'un numéro d'immatriculation étranger (ou de TVA). */
export const FOREIGN_REGISTRATION_MAX_LENGTH = 50;

export type CompanyRegistration =
  | { kind: "siren"; value: string }
  | { kind: "foreign"; value: string };

/**
 * Identification de la société saisie (décision D2) : SIREN obligatoire et valide, sauf case
 * « Société établie hors de France », qui le remplace par un numéro libre, obligatoire lui aussi.
 * `null` = saisie refusée.
 */
export function parseCompanyRegistration(input: { foreignCompany?: unknown; siren?: unknown; foreignRegistration?: unknown }): CompanyRegistration | null {
  if (input.foreignCompany === true) {
    const v = typeof input.foreignRegistration === "string" ? input.foreignRegistration.trim() : "";
    return v && v.length <= FOREIGN_REGISTRATION_MAX_LENGTH ? { kind: "foreign", value: v } : null;
  }
  return isValidSiren(input.siren) ? { kind: "siren", value: normalizeSiren(input.siren) } : null;
}

/**
 * Identification d'un compte existant, pour une acceptation dans l'application (lot 5) : SIREN du
 * compte, sinon les 9 premiers chiffres d'un SIRET valide, sinon inconnue.
 */
export function registrationOfAccount(account: { siren?: string | null; siret?: string | null }): { kind: "siren" | "unknown"; value: string | null } {
  if (account.siren && isValidSiren(account.siren)) return { kind: "siren", value: normalizeSiren(account.siren) };
  const siret = normalizeSiren(account.siret ?? "");
  if (/^\d{14}$/.test(siret) && isValidSiren(siret.slice(0, 9))) return { kind: "siren", value: siret.slice(0, 9) };
  return { kind: "unknown", value: null };
}

/**
 * L'organisateur doit-il accepter le DPA avant d'utiliser l'application (décision D1 = C) ?
 * Seul l'ORG, sur un compte qui n'a AUCUNE acceptation du DPA, quand une version est en vigueur.
 * Jamais pendant un « Voir comme » : Kestio n'accepte pas à la place du client.
 */
export function needsDpaAcceptance(p: { profileType: string | null | undefined; hasAccount: boolean; impersonated: boolean; accountHasDpa: boolean; currentDpaExists: boolean }): boolean {
  return p.profileType === "ORG" && p.hasAccount && !p.impersonated && !p.accountHasDpa && p.currentDpaExists;
}
